import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PackagePlus, PackageCheck, Clock, CheckCircle2, Trash2, ShieldCheck, Sparkles, Receipt, Printer, Mail, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { StatusTimeline } from "@/components/StatusTimeline";

const BANNER = "https://images.unsplash.com/photo-1628102491629-778571d893a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMGZvb2QlMjBncm9jZXJ5JTIwcHJvZHVjZXxlbnwwfHx8fDE3ODI5MjIwMDl8MA&ixlib=rb-4.1.0&q=85";

const statusStyle = {
  available: "bg-emerald-50 text-emerald-800 border-emerald-200",
  claimed: "bg-orange-50 text-orange-800 border-orange-200",
  scheduled: "bg-blue-50 text-blue-800 border-blue-200",
  completed: "bg-stone-100 text-stone-600 border-stone-200",
};

export default function StoreDashboard() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [receiptsData, setReceiptsData] = useState(null);

  const subActive = user?.subscription_status === "active";

  const load = useCallback(async () => {
    const [s, l] = await Promise.all([api.get("/stats"), api.get("/listings")]);
    setStats(s.data);
    setListings(l.data);
  }, []);

  const loadReceipts = useCallback(async () => {
    try { const { data } = await api.get("/store/receipts"); setReceiptsData(data); } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); loadReceipts(); api.get("/plan").then((r) => setPlan(r.data)); }, [load, loadReceipts]);

  // Handle Stripe return
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { toast.error("Payment status timed out. Refresh to check."); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Subscription activated! You can now post listings.");
          await refreshUser();
          load();
          setParams({}, { replace: true });
          return;
        }
        if (data.status === "expired") { toast.error("Payment session expired."); setParams({}, { replace: true }); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    toast.loading("Confirming your payment…", { id: "pay", duration: 2000 });
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubscribing(false);
    }
  };

  const createListing = async (payload) => {
    try {
      await api.post("/listings", payload);
      toast.success("Listing posted — nearby organizations alerted!");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeListing = async (id) => {
    await api.delete(`/listings/${id}`);
    toast.success("Listing removed");
    load();
  };

  const complete = async (id) => {
    await api.post(`/listings/${id}/complete`);
    toast.success("Marked as completed");
    load();
  };

  const printReceipt = (r, annual = false) => {
    const store = receiptsData?.store || {};
    const charity = receiptsData?.charity || "Partnership to End Addiction";
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) { toast.error("Allow pop-ups to download the receipt."); return; }
    const rows = annual
      ? (receiptsData?.receipts || []).map((x) => `<tr><td style="padding:8px 0;border-bottom:1px solid #eee">${x.date}</td><td style="padding:8px 0;border-bottom:1px solid #eee">${x.receipt_number}</td><td style="padding:8px 0;border-bottom:1px solid #eee">${x.description}</td><td style="padding:8px 0;border-bottom:1px solid #eee;text-align:right">$${x.amount.toFixed(2)}</td></tr>`).join("")
      : "";
    const body = annual
      ? `<h2 style="color:#065f46;margin:0 0 4px">Annual Donation Statement</h2>
         <p style="color:#78716c;margin:0 0 20px">${store.org_name} · ${new Date().getFullYear()}</p>
         <table style="width:100%;border-collapse:collapse;font-size:14px"><thead><tr style="text-align:left;color:#78716c;font-size:12px;text-transform:uppercase"><th style="padding-bottom:8px">Date</th><th style="padding-bottom:8px">Receipt</th><th style="padding-bottom:8px">Description</th><th style="padding-bottom:8px;text-align:right">Amount</th></tr></thead><tbody>${rows}</tbody></table>
         <p style="text-align:right;font-size:18px;font-weight:700;color:#065f46;margin-top:16px">Total donated: $${(receiptsData?.total_paid || 0).toFixed(2)}</p>`
      : `<h2 style="color:#065f46;margin:0">Payment Receipt</h2>
         <p style="color:#78716c;margin:6px 0 20px">Receipt #${r.receipt_number}</p>
         <table style="width:100%;font-size:14px;border-collapse:collapse">
           <tr><td style="padding:6px 0;color:#78716c">Billed to</td><td style="padding:6px 0;text-align:right;font-weight:600">${store.org_name || ""}</td></tr>
           <tr><td style="padding:6px 0;color:#78716c">Email</td><td style="padding:6px 0;text-align:right">${store.email || ""}</td></tr>
           <tr><td style="padding:6px 0;color:#78716c">Date</td><td style="padding:6px 0;text-align:right">${r.date}</td></tr>
           <tr><td style="padding:6px 0;color:#78716c">Description</td><td style="padding:6px 0;text-align:right">${r.description}</td></tr>
           <tr><td style="padding:14px 0 6px;border-top:1px solid #e7e5e4;color:#78716c">Amount paid</td><td style="padding:14px 0 6px;border-top:1px solid #e7e5e4;text-align:right;font-weight:700;font-size:18px;color:#065f46">$${r.amount.toFixed(2)} ${(r.currency || "usd").toUpperCase()}</td></tr>
         </table>`;
    w.document.write(`<html><head><title>Gather Receipt</title></head><body style="font-family:Arial,Helvetica,sans-serif;color:#1c1917;max-width:640px;margin:32px auto;padding:0 24px">
      <div style="font-weight:800;font-size:20px;color:#1c1917;margin-bottom:24px">Gather</div>
      ${body}
      <p style="margin-top:24px;font-size:12px;color:#78716c;line-height:1.6">100% of Gather subscription payments are a charitable donation to ${charity} and may be tax-deductible. No goods or services were exchanged for this payment.</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`);
    w.document.close();
  };

  const emailReceipt = async (r) => {
    try {
      const { data } = await api.post(`/store/receipts/${r.id}/email`);
      if (data.sent) toast.success(`Receipt emailed to ${data.email}`);
      else toast.info("Email delivery isn't configured yet — you can still download the receipt.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <DashboardShell title={`Hello, ${user?.org_name}`} subtitle="Manage the surplus food you're rescuing.">
      {/* Subscription banner */}
      {!subActive ? (
        <div data-testid="subscription-banner" className="relative overflow-hidden rounded-3xl border border-emerald-200 bg-white shadow-sm">
          <div className="grid md:grid-cols-2">
            <div className="p-8 lg:p-10">
              <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 rounded-full">Subscription required</Badge>
              <h3 className="font-heading text-2xl font-medium text-stone-900 mt-4">Activate your Monthly Rescue Plan</h3>
              <p className="text-stone-600 mt-3 leading-relaxed">
                Subscribe to start posting near-expiry and damaged food. We handle the alerts to nearby aid organizations automatically.
              </p>
              <div className="flex items-baseline gap-2 mt-6">
                <span className="font-heading text-4xl font-semibold text-emerald-800">${(plan?.amount ?? 499).toLocaleString()}</span>
                <span className="text-stone-500">/ month</span>
              </div>
              <p className="text-sm text-stone-500 mt-2">Cuts your waste-hauling and sorting costs — and 100% of it is a charitable donation to Partnership to End Addiction (tax-deductible).</p>
              <Button data-testid="subscribe-button" onClick={subscribe} disabled={subscribing} className="mt-6 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white px-8 h-11">
                <Sparkles className="w-4 h-4 mr-2" /> {subscribing ? "Redirecting…" : "Subscribe now"}
              </Button>
            </div>
            <img src={BANNER} alt="Grocery shopping" className="hidden md:block h-full w-full object-cover" />
          </div>
        </div>
      ) : (
        <div data-testid="subscription-active" className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4">
          <ShieldCheck className="w-5 h-5 text-emerald-800" />
          <p className="text-sm text-emerald-900">
            <span className="font-medium">Subscription active.</span> Valid until {user?.subscription_until ? new Date(user.subscription_until).toLocaleDateString() : "—"}.
          </p>
        </div>
      )}

      {/* Impact */}
      <div data-testid="store-impact" className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="bg-emerald-900 text-emerald-50 rounded-3xl p-8 lg:col-span-1">
          <p className="text-xs tracking-widest uppercase text-emerald-300/80">Total food donated</p>
          <p className="font-heading text-5xl font-semibold mt-2">{(stats?.donated_lbs ?? 0).toLocaleString()} <span className="text-2xl">lbs</span></p>
          <p className="text-emerald-200/80 mt-2 text-sm">Growing with every completed pickup.</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm">
          <p className="text-xs tracking-widest uppercase text-stone-500">Estimated meals</p>
          <p className="font-heading text-5xl font-semibold text-orange-700 mt-2">{(stats?.meals_donated ?? 0).toLocaleString()}</p>
          <p className="text-stone-500 mt-2 text-sm">~1.2 lbs of food per meal.</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm">
          <p className="text-xs tracking-widest uppercase text-stone-500">Carbon avoided</p>
          <p className="font-heading text-5xl font-semibold text-emerald-800 mt-2">{(stats?.carbon_kg ?? 0).toLocaleString()}<span className="text-2xl"> kg</span></p>
          <p className="text-stone-500 mt-2 text-sm">CO₂e kept from landfill (ESG).</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm">
          <p className="text-xs tracking-widest uppercase text-stone-500">Tax documentation</p>
          <p className="font-heading text-5xl font-semibold text-stone-900 mt-2">${(stats?.est_donation_value ?? 0).toLocaleString()}</p>
          <p className="text-stone-500 mt-2 text-sm">Est. deductible donation value.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat icon={PackagePlus} label="Total listings" value={stats?.total} />
        <Stat icon={PackageCheck} label="Available" value={stats?.available} accent="emerald" />
        <Stat icon={Clock} label="Claimed" value={stats?.claimed} accent="orange" />
        <Stat icon={CheckCircle2} label="Completed" value={stats?.completed} />
      </div>

      {/* Upcoming pickups */}
      {listings.some((l) => l.status === "scheduled") && (
        <div data-testid="upcoming-pickups" className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
          <h2 className="font-heading text-xl font-medium text-stone-900 flex items-center gap-2"><Clock className="w-5 h-5 text-emerald-800" /> Upcoming pickups</h2>
          <div className="mt-4 space-y-3">
            {listings.filter((l) => l.status === "scheduled").map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-4 rounded-xl bg-stone-50 border border-stone-100 p-4">
                <div>
                  <p className="font-medium text-stone-900">{l.food_type} <span className="text-stone-400 font-normal">· {l.quantity}{l.unit}</span></p>
                  <p className="text-sm text-stone-500">{l.claimed_by_name} · {l.pickup_scheduled_at}</p>
                  <div className="mt-2"><StatusTimeline status={l.status} /></div>
                </div>
                <Button data-testid={`complete-sched-${l.id}`} onClick={() => complete(l.id)} variant="outline" className="rounded-full">Mark complete</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Listings */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <h2 className="font-heading text-xl font-medium text-stone-900">Your listings</h2>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-listing-button" disabled={!subActive} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white">
                <PackagePlus className="w-4 h-4 mr-2" /> New listing
              </Button>
            </DialogTrigger>
            <ListingDialog onSubmit={createListing} />
          </Dialog>
        </div>
        <div className="divide-y divide-stone-100" data-testid="store-listings">
          {listings.length === 0 && <p className="p-6 text-stone-500">No listings yet. Post your first rescued item.</p>}
          {listings.map((l) => (
            <div key={l.id} className="p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <p className="font-medium text-stone-900">{l.food_type}</p>
                  <Badge variant="outline" className={`rounded-full capitalize ${statusStyle[l.status]}`}>{l.status}</Badge>
                </div>
                <p className="text-sm text-stone-500">
                  {l.quantity}{l.unit} · {l.condition.replace("_", " ")} · until {l.available_until}
                  {l.claimed_by_name && ` · claimed by ${l.claimed_by_name}`}
                  {l.pickup_scheduled_at && ` · pickup ${l.pickup_scheduled_at}`}
                </p>
                <StatusTimeline status={l.status} />
              </div>
              <div className="flex items-center gap-2">
                {(l.status === "claimed" || l.status === "scheduled") && (
                  <Button data-testid={`complete-${l.id}`} onClick={() => complete(l.id)} variant="outline" className="rounded-full">Mark complete</Button>
                )}
                {l.status !== "completed" && (
                  <Button data-testid={`delete-${l.id}`} onClick={() => removeListing(l.id)} variant="ghost" size="icon" className="rounded-full text-stone-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Receipts & tax documents */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="store-receipts">
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <div>
            <h2 className="font-heading text-xl font-medium text-stone-900 flex items-center gap-2"><Receipt className="w-5 h-5 text-emerald-800" /> Receipts & tax documents</h2>
            <p className="text-sm text-stone-500 mt-0.5">Every subscription payment is a tax-deductible donation. Download receipts or your annual statement.</p>
          </div>
          {(receiptsData?.receipts?.length || 0) > 0 && (
            <Button data-testid="annual-statement-btn" onClick={() => printReceipt(null, true)} variant="outline" className="rounded-full">
              <FileText className="w-4 h-4 mr-2" /> Annual statement
            </Button>
          )}
        </div>
        <div className="divide-y divide-stone-100" data-testid="receipts-list">
          {(!receiptsData || receiptsData.receipts.length === 0) && (
            <p className="p-6 text-stone-500">No payments yet. Your receipts will appear here after your first subscription payment.</p>
          )}
          {(receiptsData?.receipts || []).map((r) => (
            <div key={r.id} data-testid={`receipt-${r.id}`} className="p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <p className="font-medium text-stone-900">{r.description}</p>
                  <Badge variant="outline" className="rounded-full bg-emerald-50 text-emerald-800 border-emerald-200">paid</Badge>
                </div>
                <p className="text-sm text-stone-500 mt-0.5">#{r.receipt_number} · {r.date} · ${r.amount.toFixed(2)} {(r.currency || "usd").toUpperCase()}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid={`print-receipt-${r.id}`} onClick={() => printReceipt(r)} variant="outline" className="rounded-full"><Printer className="w-4 h-4 mr-2" />Download</Button>
                <Button data-testid={`email-receipt-${r.id}`} onClick={() => emailReceipt(r)} variant="ghost" className="rounded-full text-stone-600"><Mail className="w-4 h-4 mr-2" />Email me</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  const color = accent === "emerald" ? "text-emerald-800 bg-emerald-50" : accent === "orange" ? "text-orange-700 bg-orange-50" : "text-stone-700 bg-stone-100";
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <p className="font-heading text-3xl font-semibold text-stone-900 mt-4">{value ?? 0}</p>
      <p className="text-xs tracking-widest uppercase text-stone-500 mt-1">{label}</p>
    </div>
  );
}

function ListingDialog({ onSubmit }) {
  const [f, setF] = useState({ food_type: "", quantity: "", unit: "kg", condition: "near_expiry", available_until: "", description: "", pickup_address: "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    onSubmit({ ...f, quantity: parseFloat(f.quantity) });
  };
  return (
    <DialogContent className="rounded-2xl">
      <DialogHeader><DialogTitle className="font-heading">Post a food listing</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Food type</Label>
          <Input data-testid="listing-food-type" required value={f.food_type} onChange={set("food_type")} placeholder="Bakery items, vegetables…" className="rounded-xl h-11" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-2 col-span-2">
            <Label className="text-xs tracking-widest uppercase text-stone-500">Quantity</Label>
            <Input data-testid="listing-quantity" required type="number" step="0.1" min="0" value={f.quantity} onChange={set("quantity")} className="rounded-xl h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-stone-500">Unit</Label>
            <Select value={f.unit} onValueChange={(v) => setF((p) => ({ ...p, unit: v }))}>
              <SelectTrigger data-testid="listing-unit" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["kg", "lbs", "items", "boxes", "meals"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-stone-500">Condition</Label>
            <Select value={f.condition} onValueChange={(v) => setF((p) => ({ ...p, condition: v }))}>
              <SelectTrigger data-testid="listing-condition" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="near_expiry">Near expiry</SelectItem>
                <SelectItem value="damaged">Damaged packaging</SelectItem>
                <SelectItem value="surplus">Surplus</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-stone-500">Available until</Label>
            <Input data-testid="listing-until" required value={f.available_until} onChange={set("available_until")} placeholder="Today 6 PM" className="rounded-xl h-11" />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Pickup address (optional)</Label>
          <Input data-testid="listing-pickup" value={f.pickup_address} onChange={set("pickup_address")} placeholder="Defaults to your store address" className="rounded-xl h-11" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Notes (optional)</Label>
          <Textarea data-testid="listing-desc" value={f.description} onChange={set("description")} className="rounded-xl" />
        </div>
        <DialogFooter>
          <Button data-testid="listing-submit" type="submit" className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white w-full">Post & alert organizations</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
